/**
 * ChatInterface Component
 *
 * Purpose: Isolated chat box that maintains fixed position throughout workflow.
 * Displays progressive questions based on current workflow step.
 *
 * Layout:
 * - Fixed dimensions: ~400px width, auto height
 * - Background: formbckg.webp (right-bottom aligned, with gradient fallback)
 * - Position: Center initially, right side during split-screen
 * - Content: Progressive question display based on currentStep
 * - Animations: Smooth fade-in for each question transition
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { animateFlip } from "../utils/flip";
import { GlossyButton } from "./GlossyButton";
import "./ChatInterface.css";

const INDUSTRIES = [
  "Manufacturing",
  "Retail",
  "Services",
  "Healthcare",
  "Education",
  "Construction",
  "Technology",
  "Food & Beverage",
  "Logistics",
  "Other",
];

const COMPANY_SIZES = [
  { value: "1-3", label: "1-3 employees" },
  { value: "4-10", label: "4-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "200+", label: "200+ employees" },
];

const BUSINESS_PROCESSES = [
  // Sales & CRM
  {
    name: "Sales Management",
    keywords: ["sales", "sell", "customer", "quote", "order", "revenue"],
  },
  {
    name: "CRM - Customer Relationship",
    keywords: ["crm", "leads", "opportunities", "pipeline", "contacts"],
  },
  { name: "Quotations & Proposals", keywords: ["quote", "proposal", "estimate", "pricing"] },
  { name: "Sales Orders", keywords: ["order", "sales order", "so", "booking"] },
  { name: "Customer Portal", keywords: ["portal", "customer access", "self-service"] },

  // Finance & Accounting
  { name: "Accounting & Finance", keywords: ["accounting", "finance", "ledger", "books"] },
  { name: "Invoicing", keywords: ["invoice", "billing", "bill", "payment request"] },
  { name: "Expenses", keywords: ["expense", "reimbursement", "cost tracking", "spend"] },
  { name: "Payment Management", keywords: ["payment", "pay", "receipt", "transaction"] },
  { name: "Multi-Currency", keywords: ["currency", "forex", "exchange", "international"] },
  { name: "Bank Reconciliation", keywords: ["bank", "reconcile", "statement", "matching"] },
  { name: "Assets Management", keywords: ["assets", "depreciation", "fixed assets", "capex"] },
  { name: "Budgeting", keywords: ["budget", "forecast", "planning", "allocation"] },

  // Purchasing & Procurement
  {
    name: "Purchase Management",
    keywords: ["purchase", "buy", "vendor", "supplier", "procurement"],
  },
  { name: "Purchase Orders", keywords: ["po", "purchase order", "requisition"] },
  { name: "Vendor Management", keywords: ["vendor", "supplier", "partner", "sourcing"] },
  { name: "Purchase Agreements", keywords: ["agreement", "contract", "blanket order"] },
  { name: "Tender Management", keywords: ["tender", "rfq", "rfp", "bidding"] },

  // Inventory & Warehouse
  { name: "Inventory Management", keywords: ["inventory", "stock", "warehouse", "goods"] },
  { name: "Stock Control", keywords: ["stock control", "reorder", "safety stock", "min max"] },
  { name: "Barcode Scanning", keywords: ["barcode", "scan", "qr code", "tracking"] },
  { name: "Lot & Serial Numbers", keywords: ["lot", "serial", "batch", "traceability"] },
  { name: "Multi-Warehouse", keywords: ["warehouse", "location", "multi-location", "sites"] },
  { name: "Inventory Valuation", keywords: ["valuation", "costing", "fifo", "average cost"] },

  // Manufacturing & Production
  { name: "Manufacturing (MRP)", keywords: ["manufacturing", "production", "mrp", "work order"] },
  { name: "Bill of Materials (BOM)", keywords: ["bom", "components", "assembly", "parts list"] },
  { name: "Work Orders", keywords: ["work order", "production order", "manufacturing order"] },
  { name: "Quality Control", keywords: ["quality", "qc", "inspection", "testing", "standards"] },
  { name: "Maintenance", keywords: ["maintenance", "repair", "equipment", "preventive"] },
  { name: "Product Lifecycle (PLM)", keywords: ["plm", "engineering", "design", "version"] },

  // Project Management
  { name: "Project Management", keywords: ["project", "task", "milestone", "planning"] },
  { name: "Task Management", keywords: ["task", "todo", "assignment", "tracking"] },
  { name: "Timesheets", keywords: ["timesheet", "time tracking", "hours", "billable"] },
  {
    name: "Project Accounting",
    keywords: ["project accounting", "cost tracking", "profitability"],
  },

  // HR & Payroll
  { name: "Human Resources", keywords: ["hr", "employee", "staff", "workforce"] },
  { name: "Recruitment", keywords: ["recruitment", "hiring", "applicants", "jobs"] },
  { name: "Payroll", keywords: ["payroll", "salary", "wages", "compensation"] },
  { name: "Attendance", keywords: ["attendance", "clock", "time off", "leave"] },
  { name: "Appraisals", keywords: ["appraisal", "review", "performance", "evaluation"] },
  { name: "Employee Contracts", keywords: ["contract", "agreement", "terms", "employment"] },

  // E-commerce & Website
  { name: "Website Builder", keywords: ["website", "web", "online", "cms"] },
  { name: "E-commerce", keywords: ["ecommerce", "shop", "store", "cart", "checkout"] },
  { name: "Payment Gateway", keywords: ["payment gateway", "stripe", "paypal", "processor"] },
  { name: "Shipping Integration", keywords: ["shipping", "delivery", "courier", "tracking"] },
  { name: "Product Catalog", keywords: ["catalog", "products", "variants", "attributes"] },

  // Retail & POS
  { name: "Point of Sale (POS)", keywords: ["pos", "retail", "checkout", "cashier", "register"] },
  { name: "Restaurant POS", keywords: ["restaurant", "table", "kitchen", "orders", "menu"] },
  { name: "Loyalty Programs", keywords: ["loyalty", "rewards", "points", "discount"] },
  { name: "Gift Cards", keywords: ["gift card", "voucher", "coupon", "credit"] },

  // Marketing & Communications
  { name: "Marketing Automation", keywords: ["marketing", "campaign", "automation", "nurture"] },
  { name: "Email Marketing", keywords: ["email", "newsletter", "mailing", "blast"] },
  { name: "SMS Marketing", keywords: ["sms", "text", "messaging", "mobile"] },
  { name: "Social Media", keywords: ["social", "facebook", "linkedin", "twitter", "instagram"] },
  { name: "Events", keywords: ["event", "conference", "registration", "attendee"] },
  { name: "Surveys", keywords: ["survey", "poll", "questionnaire", "feedback"] },

  // Customer Support
  { name: "Helpdesk", keywords: ["helpdesk", "support", "ticket", "issue", "customer service"] },
  { name: "Live Chat", keywords: ["chat", "messaging", "live support", "conversation"] },
  { name: "Knowledge Base", keywords: ["knowledge", "faq", "documentation", "articles"] },
  { name: "Field Service", keywords: ["field service", "onsite", "mobile", "technician"] },

  // Supply Chain & Logistics
  { name: "Fleet Management", keywords: ["fleet", "vehicle", "transport", "logistics", "routing"] },
  { name: "Shipping Management", keywords: ["shipping", "freight", "carrier", "delivery"] },
  { name: "Route Planning", keywords: ["route", "planning", "optimization", "delivery schedule"] },

  // Business Intelligence
  { name: "Reporting & Analytics", keywords: ["report", "analytics", "dashboard", "insights"] },
  { name: "Custom Dashboards", keywords: ["dashboard", "kpi", "metrics", "visualization"] },

  // Document Management
  { name: "Documents", keywords: ["documents", "files", "storage", "sharing"] },
  { name: "Approvals", keywords: ["approval", "workflow", "sign-off", "authorization"] },
  { name: "Sign (Electronic Signature)", keywords: ["sign", "signature", "esign", "docusign"] },

  // Other
  { name: "Subscriptions", keywords: ["subscription", "recurring", "mrr", "saas"] },
  { name: "Rental Management", keywords: ["rental", "lease", "booking", "reservation"] },
  { name: "Appointment Scheduling", keywords: ["appointment", "booking", "calendar", "schedule"] },
];

export type QuestionStep =
  | "welcome"
  | "industry"
  | "companySize"
  | "businessProcesses"
  | "specificNeeds";

interface ChatInterfaceProps {
  currentStep: QuestionStep;
  industry: string;
  companySize: string;
  businessProcesses: string[];
  specificNeeds: string[];
  onStart: () => void;
  onIndustrySelect: (industry: string) => void;
  onCompanySizeSelect: (size: string) => void;
  onProcessAdd: (process: string) => void;
  onProcessRemove?: (process: string) => void;
  onNeedAdd: (need: string) => void;
  onNext: () => void;
  showNextButton: boolean;
  loading?: boolean;
  chatHistory?: Array<{ id: string; question: string; answer: string; timestamp?: number }>;
  addChatMessage?: (question: string, answer: string) => void;
  clearChatHistory?: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  currentStep,
  industry,
  companySize,
  businessProcesses,
  specificNeeds,
  onStart,
  onIndustrySelect,
  onCompanySizeSelect,
  onProcessAdd,
  onProcessRemove,
  onNeedAdd,
  onNext,
  showNextButton,
  loading = false,
  chatHistory = [],
  addChatMessage: addChatMessageProp,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [specificNeedInput, setSpecificNeedInput] = useState("");
  const [inputFocused, setInputFocused] = useState(false);
  const providedChatHistory: Array<{
    id: string;
    question: string;
    answer: string;
    timestamp?: number;
  }> = chatHistory ?? [];
  const addChatMessage = addChatMessageProp;
  const messagesRef = useRef<HTMLDivElement | null>(null);
  // Keep a ref to the latest addChatMessage so the FLIP helper can be stable
  const addChatMessageRef = useRef(addChatMessage);
  // Auto-scroll: deliberate dependency handling (disable exhaustive-deps for this effect)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    addChatMessageRef.current = addChatMessage;
  }, [addChatMessage]);

  // performAppendWithFlip is intentionally stable (no deps) and reads the latest addChatMessage from ref
  const performAppendWithFlip = useCallback(async (question: string, answer: string) => {
    const addFn = addChatMessageRef.current;
    if (typeof addFn !== "function") return;
    try {
      const source = document.querySelector(".chatbox .chat-content") as HTMLElement | null;
      addFn(question, answer);

      // wait for the DOM to update and the new message to render
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const destRoot = messagesRef.current ?? document.querySelector(".messages") ?? document.body;
      const dest = destRoot.querySelector(".chat-message-pair:last-child") as HTMLElement | null;
      if (source && dest) {
        await animateFlip(source, dest, 400);
      }
    } catch (err) {
      console.debug("performAppendWithFlip error", err);
    }
  }, []);
  const [_showChatbox, setShowChatbox] = useState(false);
  const [initialMessagePhase, setInitialMessagePhase] = useState<"hidden" | "message" | "morphed">(
    "hidden"
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const initialTimers = useRef<number[]>([]);

  // Coordinated reveal sequence:
  // Event 1 (1.5s): Chatbox reveals with horizontal wipe + "Let's configure..." text appears IMMEDIATELY
  // Event 2 (3.5s): Text moves UP to messages area, chatbox transforms to button (2s reading time)
  useEffect(() => {
    const revealDelay = 1500; // Event 1: chatbox + text appear together
    const morphDelay = 3500; // Event 2: text moves up, button appears (2s after reveal)

    // Event 1: Reveal chatbox with text immediately
    const t1 = window.setTimeout(() => {
      setShowChatbox(true);
      setInitialMessagePhase("message"); // Text appears WITH chatbox
    }, revealDelay);

    // Event 2: Move text to messages, transform to button
    const t2 = window.setTimeout(async () => {
      try {
        const welcomeText = "Let's configure your ERP requirements.";
        const exists = providedChatHistory.some(
          (m: { id: string; question: string; answer: string; timestamp?: number }) =>
            m.answer === welcomeText
        );
        if (!exists && typeof addChatMessageRef.current === "function") {
          // Perform FLIP while the source (welcome text) is still visible
          try {
            await performAppendWithFlip("", welcomeText);
          } catch (e) {
            // fallback to direct append if FLIP fails
            console.debug("performAppendWithFlip failed", e);
            const fn = addChatMessageRef.current;
            if (fn) fn("", welcomeText);
          }
        }

        // After FLIP completes, gently hide the welcome text and morph into the button
        // small buffer to allow messageIn animation to settle
        setInitialMessagePhase("hidden");

        window.setTimeout(() => {
          setInitialMessagePhase("morphed");

          // Focus button after render
          window.setTimeout(() => {
            const btn = document.querySelector(
              ".button-centered button"
            ) as HTMLButtonElement | null;
            if (btn) btn.focus();
          }, 40);
        }, 120);
      } catch (err) {
        console.debug("chat welcome append error", err);
      }
    }, morphDelay);

    initialTimers.current = [t1, t2];

    return () => {
      initialTimers.current.forEach((id) => {
        clearTimeout(id);
      });
      initialTimers.current = [];
    };
  }, [performAppendWithFlip, providedChatHistory]);

  // Add to manager-backed history when answers are provided
  useEffect(() => {
    try {
      if (
        currentStep === "companySize" &&
        industry &&
        !providedChatHistory.some(
          (m: { id: string; question: string; answer: string; timestamp?: number }) =>
            m.question === "What industry are you in?"
        )
      ) {
        if (typeof addChatMessageRef.current === "function") {
          performAppendWithFlip("What industry are you in?", industry).catch(() => {
            const fn = addChatMessageRef.current;
            if (fn) fn("What industry are you in?", industry);
          });
        }
      }

      if (
        currentStep === "businessProcesses" &&
        companySize &&
        !providedChatHistory.some(
          (m: { id: string; question: string; answer: string; timestamp?: number }) =>
            m.question === "How many employees do you have?"
        )
      ) {
        if (typeof addChatMessageRef.current === "function") {
          performAppendWithFlip("How many employees do you have?", companySize).catch(() => {
            const fn = addChatMessageRef.current;
            if (fn) fn("How many employees do you have?", companySize);
          });
        }
      }

      if (
        currentStep === "specificNeeds" &&
        businessProcesses.length > 0 &&
        !providedChatHistory.some(
          (m: { id: string; question: string; answer: string; timestamp?: number }) =>
            m.question === "What business processes do you need?"
        )
      ) {
        if (typeof addChatMessageRef.current === "function") {
          performAppendWithFlip(
            "What business processes do you need?",
            `${businessProcesses.length} processes selected`
          ).catch(() => {
            const fn = addChatMessageRef.current;
            if (fn)
              fn(
                "What business processes do you need?",
                `${businessProcesses.length} processes selected`
              );
          });
        }
      }
    } catch (err) {
      console.debug("chat append error", err);
    }
  }, [
    currentStep,
    industry,
    companySize,
    businessProcesses,
    providedChatHistory,
    performAppendWithFlip,
  ]);

  // Auto-scroll messages to bottom when DOM changes (MutationObserver avoids deps)
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return undefined;

    const doScroll = () => {
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } catch {
        el.scrollTop = el.scrollHeight;
      }
    };

    // initial scroll after small delay
    const t = window.setTimeout(doScroll, 40);

    const obs = new MutationObserver(() => {
      doScroll();
    });

    obs.observe(el, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      obs.disconnect();
    };
  }, []);

  // Filter process suggestions based on search term
  const processSuggestions = BUSINESS_PROCESSES.filter((process) => {
    if (!searchTerm.trim()) return false;
    if (businessProcesses.includes(process.name)) return false;

    const searchLower = searchTerm.toLowerCase();
    return (
      process.name.toLowerCase().includes(searchLower) ||
      process.keywords.some((keyword) => keyword.includes(searchLower))
    );
  }).slice(0, 5);

  // Click-outside handler removed - using blur event with timeout instead to avoid conflicts

  const handleProcessAdd = (processName: string) => {
    onProcessAdd(processName);
    setSearchTerm("");
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  };

  const handleSpecificNeedAdd = () => {
    if (specificNeedInput.trim()) {
      onNeedAdd(specificNeedInput.trim());
      setSpecificNeedInput("");
    }
  };

  return (
    <div className="chat-panel">
      {/* Welcome Heading - Always visible */}
      <div className="welcome-container">
        <h1 className="chat-heading">Welcome to aiia!</h1>
      </div>

      {/* Chat area: messages (history) + chatbox (current) */}
      <div className="chatArea">
        {/* Messages - chronological history (grows upward) */}
        <div className="messages" aria-live="polite" aria-atomic="false">
          {providedChatHistory.length > 0 && (
            <div className="chat-scrollable" ref={messagesRef}>
              {providedChatHistory.map(
                (msg: { id: string; question: string; answer: string; timestamp?: number }) => (
                  <div key={msg.id} className="chat-message-pair">
                    {msg.question && (
                      <div className="chat-question">
                        <div className="text-white/60 text-xs font-normal mb-1">{msg.question}</div>
                      </div>
                    )}
                    <div className="chat-answer">
                      <div className="text-white text-sm font-normal">{msg.answer}</div>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Chatbox - single multi-state element always at bottom */}
        <div className={`chatbox ${_showChatbox ? "revealed" : "hidden"}`}>
          <div className="chat-content">
            {/* Welcome State: Frame 2 text → Frame 3 button */}
            {currentStep === "welcome" && (
              <>
                {initialMessagePhase === "message" && (
                  <div className="welcome-text-display">
                    <p className="text-white text-base font-normal text-center">
                      Let's configure your ERP requirements.
                    </p>
                  </div>
                )}

                {initialMessagePhase === "morphed" && (
                  <div className="button-centered">
                    <GlossyButton
                      label="Click here to start!"
                      onClick={() => {
                        initialTimers.current.forEach((id) => {
                          clearTimeout(id);
                        });
                        initialTimers.current = [];
                        onStart();
                      }}
                      loading={loading}
                      className="full-width-button"
                    />
                  </div>
                )}
              </>
            )}

            {/* Industry Selection */}
            {currentStep === "industry" && (
              <div className="active-question-container">
                <div className={`question-title ${industry || inputFocused ? "visible" : ""}`}>
                  What industry are you in?
                </div>
                <select
                  value={industry}
                  onChange={(e) => onIndustrySelect(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  className="chat-select"
                  disabled={loading}
                >
                  <option value="">What industry are you in?</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>
                      {ind}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Company Size Selection */}
            {currentStep === "companySize" && (
              <div className="active-question-container">
                <div className={`question-title ${companySize || inputFocused ? "visible" : ""}`}>
                  How many employees do you have?
                </div>
                <select
                  value={companySize}
                  onChange={(e) => onCompanySizeSelect(e.target.value)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  className="chat-select"
                  disabled={loading}
                >
                  <option value="">How many employees do you have?</option>
                  {COMPANY_SIZES.map((size) => (
                    <option key={size.value} value={size.value}>
                      {size.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Business Processes Selection */}
            {currentStep === "businessProcesses" && (
              <div className="active-question-container">
                {/* Title always visible */}
                <div className="question-title visible">What business processes do you need?</div>

                {/* Process Pills - displayed above search bar */}
                {businessProcesses.length > 0 && (
                  <div className="process-pills-container">
                    {businessProcesses.map((process) => (
                      <button
                        key={process}
                        type="button"
                        onClick={() => {
                          if (onProcessRemove) {
                            onProcessRemove(process);
                          }
                        }}
                        className="process-pill"
                      >
                        <span>{process}</span>
                        <span className="pill-remove">×</span>
                      </button>
                    ))}
                  </div>
                )}

                <div className="relative">
                  {/* Next button inside the input (integrated search bar) - show after 1+ selections */}
                  {showNextButton && businessProcesses.length > 0 && (
                    <div className="next-button-inside">
                      <GlossyButton label="Next" onClick={onNext} loading={loading} />
                    </div>
                  )}

                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => {
                      setInputFocused(true);
                      setShowSuggestions(true);
                      // Clear placeholder immediately on focus
                      if (searchTerm === "") {
                        // Placeholder will be cleared by focus state
                      }
                    }}
                    onBlur={() => {
                      setInputFocused(false);
                      // Delay closing to allow mouseDown to fire first
                      setTimeout(() => setShowSuggestions(false), 150);
                    }}
                    placeholder={inputFocused ? "" : "What business processes do you need?"}
                    className={`chat-input ${showNextButton && businessProcesses.length > 0 ? "with-next" : ""}`}
                    disabled={loading}
                  />

                  {/* Suggestions dropdown */}
                  {showSuggestions && processSuggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                      {processSuggestions.map((process) => (
                        <button
                          key={process.name}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleProcessAdd(process.name);
                          }}
                          className="suggestion-item"
                        >
                          <div className="suggestion-title">{process.name}</div>
                          <div className="suggestion-keywords">
                            {process.keywords.slice(0, 3).join(", ")}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Specific Needs (Optional) */}
            {currentStep === "specificNeeds" && (
              <div className="active-question-container">
                <div
                  className={`question-title ${specificNeedInput || inputFocused ? "visible" : ""}`}
                >
                  Any specific needs? (Optional)
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={specificNeedInput}
                    onChange={(e) => setSpecificNeedInput(e.target.value)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSpecificNeedAdd();
                      }
                    }}
                    placeholder="Any specific needs? (Optional)"
                    className="chat-input flex-1"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={handleSpecificNeedAdd}
                    className="add-button"
                    disabled={!specificNeedInput.trim() || loading}
                  >
                    Add
                  </button>
                </div>

                <div className="counter-text">
                  Added {specificNeeds.length} specific need{specificNeeds.length !== 1 ? "s" : ""}
                </div>
              </div>
            )}

            {/* Next Button (shows when appropriate) - not used for businessProcesses (it uses inside-input variant) */}
            {showNextButton && currentStep !== "businessProcesses" && (
              <div className="next-button-container">
                <GlossyButton
                  label="Next"
                  onClick={onNext}
                  loading={loading}
                  className="full-width-button"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
